import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Check, Sparkles, Users, Brain, Crown, Zap, Shield,
    TrendingUp, FileText, MessageCircle, Bell, Heart, Loader2, CreditCard
} from 'lucide-react';

const PricingPage = () => {
    const { user, api, refreshUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loading, setLoading] = useState(null);
    const [checkingPayment, setCheckingPayment] = useState(false);
    
    const currentTier = user?.subscription?.tier || 'basic';
    const currentAddons = user?.subscription?.addons || [];

    // Check for payment success/cancel on page load
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        const paymentStatus = searchParams.get('payment');
        
        if (sessionId) {
            pollPaymentStatus(sessionId);
        } else if (paymentStatus === 'cancelled') {
            toast.info('Payment was cancelled. You can try again anytime.');
            setSearchParams({});
        }
    }, []);

    const pollPaymentStatus = async (sessionId, attempts = 0) => {
        const maxAttempts = 5;
        const pollInterval = 2000;

        if (attempts >= maxAttempts) {
            toast.error('Payment status check timed out. Please refresh the page or contact support.');
            setCheckingPayment(false);
            setSearchParams({});
            return;
        }

        setCheckingPayment(true);
        
        try {
            const response = await api.get(`/payments/status/${sessionId}`);
            const data = response.data;

            if (data.payment_status === 'paid') {
                toast.success('Payment successful! Your subscription has been upgraded.');
                setCheckingPayment(false);
                setSearchParams({});
                // Refresh user data to get new subscription
                if (refreshUser) {
                    await refreshUser();
                } else {
                    window.location.reload();
                }
                return;
            } else if (data.status === 'expired') {
                toast.error('Payment session expired. Please try again.');
                setCheckingPayment(false);
                setSearchParams({});
                return;
            }

            // Continue polling if still pending
            setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
        } catch (error) {
            console.error('Error checking payment status:', error);
            toast.error('Error checking payment status. Please refresh the page.');
            setCheckingPayment(false);
            setSearchParams({});
        }
    };

    const tiers = [
        {
            id: 'basic',
            name: 'MoodTrack Basic',
            price: { monthly: 0, yearly: 0 },
            description: 'Start your wellness journey',
            features: [
                { text: 'Daily mood check-ins', icon: Heart },
                { text: 'Basic journaling (unlimited text)', icon: FileText },
                { text: 'Mood history (7 days)', icon: TrendingUp },
                { text: '1 accountability partner', icon: Users },
                { text: 'Basic weekly summary', icon: Sparkles },
                { text: 'Journal lock (PIN/password)', icon: Shield },
                { text: 'Basic push notifications', icon: Bell }
            ],
            cta: 'Current Plan',
            popular: false
        },
        {
            id: 'plus',
            name: 'MoodTrack Plus',
            price: { monthly: 7.99, yearly: 59.99 },
            savings: '37%',
            description: 'Turn your emotions into clarity',
            subheadline: 'Understand why you feel the way you do - not just log it',
            features: [
                { text: 'Everything in Basic', icon: Check },
                { text: 'Unlimited mood & journal history', icon: TrendingUp },
                { text: 'AI mood trends & patterns', icon: Brain },
                { text: 'Mood streaks & tracking', icon: Zap },
                { text: 'Custom mood tags', icon: Sparkles },
                { text: 'Search & filter entries', icon: FileText },
                { text: 'AI journal insights', icon: Brain },
                { text: 'Export (PDF, CSV)', icon: FileText },
                { text: 'Up to 3 accountability partners', icon: Users },
                { text: 'Partners Chat', icon: MessageCircle },
                { text: 'Scheduled check-ins', icon: Bell },
                { text: 'Shared progress summaries', icon: TrendingUp }
            ],
            valueProps: [
                'Discover emotional patterns across weeks & months',
                'Get weekly AI reflections that explain your moods',
                'Stay accountable with people who support you',
                'Protect your private thoughts with Face ID'
            ],
            cta: 'Upgrade to Plus',
            popular: true
        },
        {
            id: 'complete',
            name: 'MoodTrack Complete',
            price: { monthly: 12.99, yearly: 89.99 },
            savings: '42%',
            description: 'The complete wellness experience',
            features: [
                { text: 'Everything in Plus', icon: Check },
                { text: 'Unlimited accountability partners', icon: Users },
                { text: 'AI coping suggestions', icon: Heart },
                { text: 'Personalized affirmations', icon: Sparkles },
                { text: 'Burnout & fatigue signals', icon: Brain },
                { text: 'Monthly Wellness Report', icon: FileText },
                { text: 'Trigger detection', icon: Brain },
                { text: 'AI reflection prompts', icon: Sparkles },
                { text: '"What This Week Means" insights', icon: Brain },
                { text: 'Accountability streaks', icon: Zap }
            ],
            cta: 'Get Complete',
            popular: false
        }
    ];

    const addons = [
        {
            id: 'partners_pro',
            name: 'Partners Pro',
            price: 2.99,
            headline: 'Growth is easier with support',
            subheadline: 'Upgrade your accountability system to stay consistent and supported',
            features: [
                'Unlimited accountability partners',
                'Group accountability circles and chats',
                'Missed accountability check-ins',
                'Accountability streaks'
            ]
        },
        {
            id: 'deeper_insights',
            name: 'Deeper Insights',
            price: 4.99,
            headline: 'Understand your emotions on a deeper level',
            subheadline: 'Turn your journal into meaningful self-awareness',
            features: [
                'Personalized emotional summaries',
                'Trigger & pattern detection',
                'Monthly mental wellness report',
                'Burnout & emotional fatigue signals'
            ]
        }
    ];

    const handleUpgrade = async (tierId) => {
        if (tierId === 'basic') return;
        
        setLoading(tierId);
        try {
            // Use Stripe checkout for paid tiers
            const response = await api.post('/payments/checkout', {
                tier: tierId,
                billing_cycle: billingCycle,
                origin_url: window.location.origin
            });

            if (response.data.url) {
                // Redirect to Stripe checkout
                window.location.href = response.data.url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Checkout error:', error);
            if (error.response?.status === 500 && error.response?.data?.detail?.includes('Payment system not configured')) {
                toast.error('Payment system is being configured. Please try again later.');
            } else {
                toast.error(error.response?.data?.detail || 'Failed to start checkout. Please try again.');
            }
            setLoading(null);
        }
    };

    const handleAddAddon = async (addonId) => {
        setLoading(addonId);
        try {
            await api.post(`/subscription/add-addon?addon_id=${addonId}`);
            toast.success(`Added ${addons.find(a => a.id === addonId)?.name}!`);
            window.location.reload();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add addon');
        } finally {
            setLoading(null);
        }
    };

    return (
        <DashboardLayout>
            <div data-testid="pricing-page" className="max-w-6xl mx-auto space-y-8">
                {/* Payment Processing Overlay */}
                {checkingPayment && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                        <Card className="glass-card p-8 max-w-md mx-4">
                            <div className="flex flex-col items-center gap-4">
                                <Loader2 className="w-12 h-12 animate-spin text-primary" />
                                <h3 className="font-heading text-xl">Processing Payment...</h3>
                                <p className="text-muted-foreground text-center">
                                    Please wait while we confirm your payment.
                                </p>
                            </div>
                        </Card>
                    </div>
                )}

                {/* Header */}
                <div className="text-center space-y-4">
                    <h1 className="font-heading text-4xl font-semibold text-foreground">
                        Choose Your Journey
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                        Unlock deeper insights, better support, and more tools for your mental wellness
                    </p>
                    
                    {/* Billing Toggle */}
                    <div className="flex items-center justify-center gap-4 pt-4">
                        <span className={billingCycle === 'monthly' ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                            Monthly
                        </span>
                        <Switch
                            checked={billingCycle === 'yearly'}
                            onCheckedChange={(checked) => setBillingCycle(checked ? 'yearly' : 'monthly')}
                            data-testid="billing-toggle"
                        />
                        <span className={billingCycle === 'yearly' ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                            Yearly
                        </span>
                        {billingCycle === 'yearly' && (
                            <span className="px-3 py-1 rounded-full bg-chart-1/20 text-chart-1 text-sm font-medium">
                                Save up to 42%
                            </span>
                        )}
                    </div>
                </div>

                {/* Pricing Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {tiers.map((tier, index) => (
                        <motion.div
                            key={tier.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.1 }}
                        >
                            <Card className={`glass-card h-full relative ${
                                tier.popular ? 'ring-2 ring-primary shadow-xl' : ''
                            }`}>
                                {tier.popular && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                        <span className="px-4 py-1 rounded-full bg-primary text-primary-foreground text-sm font-medium">
                                            Most Popular
                                        </span>
                                    </div>
                                )}
                                <CardHeader className="text-center pt-8">
                                    <CardTitle className="font-heading text-2xl">{tier.name}</CardTitle>
                                    <p className="text-muted-foreground">{tier.description}</p>
                                    <div className="pt-4">
                                        <span className="font-heading text-4xl font-semibold">
                                            ${billingCycle === 'yearly' ? tier.price.yearly : tier.price.monthly}
                                        </span>
                                        <span className="text-muted-foreground">
                                            /{billingCycle === 'yearly' ? 'year' : 'month'}
                                        </span>
                                        {tier.savings && billingCycle === 'yearly' && (
                                            <p className="text-chart-1 text-sm mt-1">Save {tier.savings}</p>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <ul className="space-y-3">
                                        {tier.features.map((feature, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <feature.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                                                <span className="text-sm">{feature.text}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    
                                    <Button
                                        className={`w-full rounded-full ${tier.popular ? '' : 'variant-outline'}`}
                                        variant={tier.id === currentTier ? 'outline' : tier.popular ? 'default' : 'outline'}
                                        disabled={tier.id === currentTier || tier.id === 'basic' || loading === tier.id}
                                        onClick={() => handleUpgrade(tier.id)}
                                        data-testid={`upgrade-${tier.id}`}
                                    >
                                        {loading === tier.id ? (
                                            <>
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                Redirecting to checkout...
                                            </>
                                        ) : tier.id === currentTier ? (
                                            'Current Plan'
                                        ) : tier.id === 'basic' ? (
                                            'Free Plan'
                                        ) : (
                                            <>
                                                <CreditCard className="w-4 h-4 mr-2" />
                                                {tier.cta}
                                            </>
                                        )}
                                    </Button>
                                </CardContent>
                            </Card>
                        </motion.div>
                    ))}
                </div>

                {/* Add-ons Section */}
                <div className="pt-8">
                    <h2 className="font-heading text-2xl font-semibold text-center mb-6">
                        Enhance Your Experience
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {addons.map((addon) => (
                            <Card key={addon.id} className="glass-card">
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1">
                                            <h3 className="font-heading text-xl font-semibold mb-1">
                                                {addon.name}
                                            </h3>
                                            <p className="text-primary font-medium mb-2">
                                                +${addon.price}/month
                                            </p>
                                            <p className="text-muted-foreground text-sm mb-4">
                                                {addon.subheadline}
                                            </p>
                                            <ul className="space-y-2">
                                                {addon.features.map((feature, i) => (
                                                    <li key={i} className="flex items-center gap-2 text-sm">
                                                        <Check className="w-4 h-4 text-chart-1" />
                                                        {feature}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                        <Button
                                            variant={currentAddons.includes(addon.id) ? 'outline' : 'default'}
                                            className="rounded-full"
                                            disabled={currentAddons.includes(addon.id) || loading === addon.id}
                                            onClick={() => handleAddAddon(addon.id)}
                                            data-testid={`addon-${addon.id}`}
                                        >
                                            {currentAddons.includes(addon.id) ? 'Active' : 'Add'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>

                {/* Note */}
                <p className="text-center text-muted-foreground text-sm">
                    You can upgrade or cancel anytime. All plans include a 7-day free trial.
                </p>
            </div>
        </DashboardLayout>
    );
};

export default PricingPage;
